import React, { useState, useRef } from 'react';
import { Mic, Sparkles, RotateCcw, Download, Music, Zap } from 'lucide-react';
import { useAudioEngine } from '../hooks/useAudioEngine';
import AudioPlayer from '../utils/AudioPlayer';

const VoiceEnhancer = ({ setIsExporting, setExportProgress }) => {
  const [audioFile, setAudioFile] = useState(null);
  const [enhancedUrl, setEnhancedUrl] = useState(null);
  const previewAudioRef = useRef(null);
  
  const { enhanceVoice, validateFileDuration } = useAudioEngine(null, null);

  const handleProcess = async () => {
    if (!audioFile) return;
    const url = await enhanceVoice(audioFile, setIsExporting, setExportProgress);
    if (url) setEnhancedUrl(url);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // We keep our 15-minute safety limit
    const check = await validateFileDuration(file, 15);
    if (!check.isValid) {
      alert(check.msg);
      return;
    }
    setAudioFile(file);
  };

  const reset = () => {
    setAudioFile(null);
    setEnhancedUrl(null);
  };

  return (
    <div className="max-w-xl mx-auto text-center space-y-10 animate-in fade-in duration-500">
      {/* HEADER */}
      <div className="space-y-4">
        <div className="inline-block p-3 bg-indigo-600/10 rounded-2xl border border-indigo-500/20 mb-2">
          <Mic className="text-indigo-500" size={24} />
        </div>
        <h2 className="text-4xl font-black uppercase tracking-tighter text-white italic">Voice Enhancer</h2>
        <p className="text-slate-400 max-w-sm mx-auto">
          Apply professional studio processing to your voice recordings instantly.
        </p>
      </div>

      {!enhancedUrl ? (
        <div className="space-y-8">
          {!audioFile ? (
            /* UPLOAD ZONE */
            <div className="p-20 border-2 border-dashed border-slate-800 bg-slate-900/30 rounded-[50px] hover:border-indigo-600 transition-all cursor-pointer relative group overflow-hidden">
               <div className="absolute inset-0 bg-indigo-600/5 opacity-0 group-hover:opacity-100 transition-opacity" />
               <Zap className="mx-auto mb-6 text-slate-700 group-hover:text-indigo-500 group-hover:scale-110 transition-all" size={64} />
               <input type="file" accept="audio/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleUpload} />
               <p className="font-bold text-slate-500 uppercase tracking-[0.2em] text-[10px] group-hover:text-white">
                  Drop Audio to Enhance
               </p>
            </div>
          ) : (
            /* ACTION CARD */
            <div className="bg-slate-900 p-10 rounded-[50px] border border-slate-800 shadow-2xl space-y-8 animate-in zoom-in duration-300">
              <div className="space-y-2">
                <Music className="text-indigo-500 mx-auto" size={32} />
                <h3 className="text-lg font-bold text-white truncate px-6">{audioFile.name}</h3>
              </div>
              
              <button 
                onClick={handleProcess} 
                className="w-full bg-white text-black py-6 rounded-3xl font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-indigo-500 hover:text-white transition-all active:scale-95 shadow-xl"
              >
                <Sparkles size={20} /> Magic Studio Enhance
              </button>

              <button onClick={() => setAudioFile(null)} className="text-slate-500 uppercase text-[10px] font-black hover:text-white transition-colors flex items-center gap-2 mx-auto">
                <RotateCcw size={12} /> Choose different file
              </button>
            </div>
          )}
        </div>
      ) : (
        /* PREVIEW SECTION */
        <div className="bg-slate-900 p-10 rounded-[60px] border-2 border-indigo-500/30 space-y-10 animate-in zoom-in duration-300">
          <div className="space-y-2">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-500/20">
              <Sparkles className="text-green-500" size={24} />
            </div>
            <h3 className="text-2xl font-black text-white uppercase tracking-tight">Vocal Polish Ready</h3>
            <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest">Enhanced with Pro Mic EQ & Compression</p>
          </div>
          
          <AudioPlayer file={enhancedUrl} audioRef={previewAudioRef} />

          <div className="grid grid-cols-2 gap-4">
            <button 
              onClick={reset} 
              className="p-5 bg-slate-800 text-white rounded-[2rem] font-black text-[10px] uppercase tracking-widest hover:bg-slate-700 transition-all flex flex-col items-center gap-2"
            >
              <RotateCcw size={18} /> Start Over
            </button>
            <a 
              href={enhancedUrl} 
              download={`vidabol-enhanced-${Date.now()}.mp3`} 
              className="p-5 bg-indigo-600 text-white rounded-[2rem] font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-all shadow-lg shadow-indigo-500/20 flex flex-col items-center gap-2"
            >
              <Download size={18} /> Download Pro MP3
            </a>
          </div>
        </div>
      )}

      {/* PRO TIP BOX */}
      {!enhancedUrl && (
        <div className="p-6 bg-slate-900/50 rounded-3xl border border-slate-800 flex items-start gap-4 text-left">
          <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
             <Zap className="text-amber-500" size={16} />
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed font-medium">
            <strong className="text-slate-300 block mb-1 uppercase tracking-widest">Pro Tip</strong>
            This tool adds warmth (bass) and clarity (treble) while normalizing your volume for a professional broadcast sound.
          </p>
        </div>
      )}
    </div>
  );
};

export default VoiceEnhancer;